import { Avatar, Flex, Spacer } from 'components'
import { useWorkspace } from 'components/providers'
import { PermissionValue } from 'types'
import { useUsers } from 'utils'
import { Menu, MenuProps, Text } from '.'

interface Props<V extends string, T extends boolean> extends Omit<MenuProps<V, T>, 'onChange'> {
  onChange: (userId: T extends true ? string[] : string) => void
  permissionsAllowed?: Array<PermissionValue>
  userDetail?: Record<string, Detail>
}

interface Detail {
  hideFromValue?: boolean
  title?: string
}

export default function UserSelect<V extends string, T extends boolean = false>({
  isMulti,
  permissionsAllowed,
  userDetail,
  value,
  ...rest
}: Props<V, T>) {
  const { workspace } = useWorkspace()

  const userById = workspace?.userById ?? {}
  let filteredUserIds = Object.keys(userById)
  if (permissionsAllowed) {
    filteredUserIds = filteredUserIds.filter(
      userId => userId && permissionsAllowed!.includes(userById[userId]?.permission)
    )
  }
  const { data: users } = useUsers(filteredUserIds)

  const selectedUsers = users
    ? typeof value === 'string'
      ? [users.find(u => u?.id === value)]
      : value?.map(v => users.find(u => u?.id === v))
    : undefined
  const firstUser = selectedUsers?.[0]
  const firstThreeUsers = selectedUsers
    ?.filter(u => u?.id && !userDetail?.[u?.id]?.hideFromValue)
    ?.slice(0, 3)

  return (
    <Menu
      isMulti={isMulti}
      width="100%"
      variant="outline"
      menuProps={{ positioning: { sameWidth: true } }}
      buttonLabel={
        value?.length && firstUser ? (
          <Flex align="center" gap={2}>
            <Flex gap={2} flexWrap="wrap">
              {isMulti ? (
                <Flex align="center" gap={2} overflow="hidden">
                  <Flex>
                    {firstThreeUsers?.map((user, index) => (
                      <Avatar
                        key={user?.id}
                        ml={index && '-10px'}
                        src={user?.photoUrl}
                        name={user?.name}
                        size="sm"
                      />
                    ))}
                    {selectedUsers.length > 3 && (
                      <Flex
                        align="center"
                        bg="gray.400"
                        borderRadius="full"
                        color="white"
                        height="32px"
                        justify="center"
                        ml="-10px"
                        width="32px"
                        zIndex={1}
                      >
                        +{selectedUsers.length - 3}
                      </Flex>
                    )}
                  </Flex>
                  <Text textStyle="bodySmall" fontWeight="500">
                    {selectedUsers?.length} Members
                  </Text>
                </Flex>
              ) : (
                <Flex align="center" gap={2} overflow="hidden">
                  <Avatar src={firstUser?.photoUrl} name={firstUser?.name} size="sm" />
                  <Text textStyle="bodySmall" fontWeight="500">
                    {firstUser?.name}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        ) : (
          <Text textStyle="bodySmall" fontWeight="500">
            Select...
          </Text>
        )
      }
      options={
        users?.map(user => {
          const { displayName, id = '', photoUrl } = user || {}
          const { title } = userDetail?.[id] || {}

          return {
            value: id,
            label: (
              <Flex align="center" flex={1} gap={2}>
                <Avatar name={displayName} src={photoUrl} size="sm" />
                <Text textStyle="bodySmall" fontWeight="500">
                  {displayName}
                </Text>
                {title && (
                  <>
                    <Spacer />
                    <Text textStyle="bodySmall" color="gray.500">
                      {title}
                    </Text>
                  </>
                )}
              </Flex>
            ),
          }
        }) ?? []
      }
      value={value}
      {...rest}
    />
  )
}
